import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { STEPS, EMPTY_MUSE, toPayload } from "@/components/muse/steps";
import UpgradeModal from "@/components/UpgradeModal";

export default function MuseWizard() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [m, setM] = useState(EMPTY_MUSE);
  const [step, setStep] = useState(Number(params.get("step")) || 1);
  const [busy, setBusy] = useState(false);
  const [upsell, setUpsell] = useState(false);

  useEffect(() => {
    if (id) api.get(`/muses/${id}`).then(({ data }) => setM(data)).catch(() => nav("/muses"));
  }, [id, nav]);

  const set = (k, v) => setM((p) => ({ ...p, [k]: v }));
  const { title, sub, C } = STEPS[step - 1];

  const save = async () => {
    if (step === 1 && (!m.name.trim() || !m.look_description.trim())) {
      toast.error("Name and look description are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...toPayload(m), wizard_step: Math.max(step + (step < 5 ? 1 : 0), m.wizard_step || 1) };
      const { data } = id ? await api.put(`/muses/${id}`, payload) : await api.post("/muses", payload);
      setM(data);
      if (step === 5) {
        toast.success(`${data.name}'s bible is complete`);
        nav(`/muses/${data.id}`);
      } else {
        toast.success("Progress saved");
        if (!id) nav(`/muses/${data.id}/build?step=${step + 1}`, { replace: true });
        setStep(step + 1);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      if (err?.response?.data?.detail === "MUSE_LIMIT_REACHED") setUpsell(true);
      else toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <button className="text-xs uppercase tracking-[0.15em] muted flex items-center gap-2 hover:text-[#1A1325]"
          onClick={() => (step > 1 ? setStep(step - 1) : nav(id ? `/muses/${id}` : "/muses"))} data-testid="wizard-back-button">
          <ArrowLeft size={14} /> {step > 1 ? "Back" : "Exit"}
        </button>
        <span className="text-xs muted" data-testid="wizard-step-indicator">Step {step} of 5</span>
      </div>
      <div className="flex gap-1.5 mb-10" data-testid="wizard-progress-bar">
        {STEPS.map((s, i) => (
          <div key={s.title} className="h-[3px] flex-1 rounded-full overflow-hidden bg-[#F0EAF7]">
            <div className="h-full bg-[#9C4DDC] transition-[width] duration-500" style={{ width: i < step ? "100%" : "0%" }} />
          </div>
        ))}
      </div>
      <div key={step} className="fade-up">
        <p className="eyebrow mb-2">{id ? m.name || "New muse" : "Create your first muse"}</p>
        <h1 className="text-4xl sm:text-5xl mb-2" data-testid="wizard-step-title">{title}</h1>
        <p className="muted text-sm mb-10">{sub}</p>
        <C m={m} set={set} ro={false} />
      </div>
      <div className="sticky bottom-20 md:bottom-6 mt-12 z-30">
        <button className="btn-primary w-full py-4 shadow-[0_10px_40px_-10px_rgba(156,77,220,.35)]" onClick={save} disabled={busy} data-testid="wizard-save-continue-button">
          {busy ? "Saving…" : step === 5 ? "Save & finish" : "Save & continue"}
        </button>
      </div>
      <UpgradeModal open={upsell} onOpenChange={setUpsell} />
    </div>
  );
}
